import React, { useState, useMemo } from "react";
import { format, parseISO } from "date-fns";
import {
    FileText, Search, Package, DollarSign, Eye
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function HistoricoNotasFiscais({ insumos = [], movimentacoes = [] }) {
    const [searchTerm, setSearchTerm] = useState("");

    const formatCurrency = (value) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);

    // Build NF list from insumos with nota_fiscal and from movimentacoes with NF references
    const notasFiscais = useMemo(() => {
        const nfMap = new Map();

        // Collect from movimentacoes that reference NFs in observacao
        movimentacoes.forEach(m => {
            if (m.tipo !== "entrada") return;
            const obs = m.observacao || "";
            const nfMatch = obs.match(/NF[:\s]*([^\s-]+)/i);
            if (!nfMatch) return;

            const nfNumero = nfMatch[1].trim();
            if (!nfNumero) return;

            const insumo = insumos.find(i => i.id === m.insumo_id);

            if (!nfMap.has(nfNumero)) {
                nfMap.set(nfNumero, {
                    numero: nfNumero,
                    data: m.data || m.created_at,
                    itens: [],
                    valorTotal: 0
                });
            }

            const nf = nfMap.get(nfNumero);
            nf.itens.push({
                nome: insumo?.nome || "Insumo removido",
                quantidade: m.quantidade,
                unidade: insumo?.unidade || "",
                custo: m.custo_total || 0
            });
            nf.valorTotal += (m.custo_total || 0);
        });

        // Also collect from insumos with nota_fiscal field
        insumos.forEach(insumo => {
            if (!insumo.nota_fiscal) return;
            const nfNumero = insumo.nota_fiscal;

            if (!nfMap.has(nfNumero)) {
                nfMap.set(nfNumero, {
                    numero: nfNumero,
                    data: insumo.created_at || insumo.updated_at,
                    itens: [],
                    valorTotal: 0,
                    fornecedor: insumo.fornecedor
                });
            }

            const nf = nfMap.get(nfNumero);
            // Check if this insumo is already listed
            const alreadyListed = nf.itens.some(item => item.nome === insumo.nome);
            if (!alreadyListed) {
                nf.itens.push({
                    nome: insumo.nome,
                    quantidade: insumo.quantidade_estoque,
                    unidade: insumo.unidade,
                    custo: (insumo.quantidade_estoque || 0) * (insumo.custo_unitario || 0)
                });
                if (!nf.valorTotal) {
                    nf.valorTotal += (insumo.quantidade_estoque || 0) * (insumo.custo_unitario || 0);
                }
            }
            if (!nf.fornecedor && insumo.fornecedor) {
                nf.fornecedor = insumo.fornecedor;
            }
        });

        return Array.from(nfMap.values())
            .sort((a, b) => new Date(b.data || 0) - new Date(a.data || 0));
    }, [insumos, movimentacoes]);

    const filtered = notasFiscais.filter(nf => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            nf.numero.toLowerCase().includes(term) ||
            nf.fornecedor?.toLowerCase().includes(term) ||
            nf.itens.some(item => item.nome.toLowerCase().includes(term))
        );
    });

    const totalNFs = notasFiscais.length;
    const valorTotalNFs = notasFiscais.reduce((acc, nf) => acc + (nf.valorTotal || 0), 0);

    return (
        <div className="space-y-6">
            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100 border-indigo-200">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-indigo-600 text-sm font-medium">Notas Registradas</p>
                                <p className="text-2xl font-bold text-indigo-800 mt-1">{totalNFs}</p>
                            </div>
                            <FileText className="w-10 h-10 text-indigo-300" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200">
                    <CardContent className="p-5">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-emerald-600 text-sm font-medium">Valor Total</p>
                                <p className="text-2xl font-bold text-emerald-800 mt-1">{formatCurrency(valorTotalNFs)}</p>
                            </div>
                            <DollarSign className="w-10 h-10 text-emerald-300" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search */}
            <div className="bg-white rounded-2xl border border-stone-200 p-4">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" />
                    <Input
                        placeholder="Buscar por número da NF, fornecedor ou insumo..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                    />
                </div>
            </div>

            {/* NF Cards */}
            <div className="space-y-4">
                {filtered.map((nf, idx) => (
                    <Card key={`${nf.numero}-${idx}`} className="bg-white border-stone-200 overflow-hidden hover:shadow-md transition-all">
                        <CardContent className="p-0">
                            {/* NF Header */}
                            <div className="flex items-center justify-between p-4 bg-stone-50 border-b border-stone-200">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                                        <FileText className="w-5 h-5 text-indigo-600" />
                                    </div>
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <h3 className="font-bold text-stone-800">NF {nf.numero}</h3>
                                            <Badge variant="outline" className="text-xs">
                                                {nf.itens.length} {nf.itens.length === 1 ? "item" : "itens"}
                                            </Badge>
                                        </div>
                                        <div className="flex items-center gap-3 text-sm text-stone-500 mt-0.5">
                                            {nf.data && (
                                                <span>{format(parseISO(nf.data), "dd/MM/yyyy")}</span>
                                            )}
                                            {nf.fornecedor && (
                                                <span>• {nf.fornecedor}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <div className="text-right">
                                    <p className="text-xs text-stone-500">Valor Total</p>
                                    <p className="text-lg font-bold text-stone-800">{formatCurrency(nf.valorTotal)}</p>
                                </div>
                            </div>

                            {/* NF Items */}
                            <div className="p-4">
                                <Table>
                                    <TableHeader>
                                        <TableRow className="border-none">
                                            <TableHead className="text-xs h-8">Insumo</TableHead>
                                            <TableHead className="text-xs text-right h-8">Quantidade</TableHead>
                                            <TableHead className="text-xs text-right h-8">Valor</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {nf.itens.map((item, itemIdx) => (
                                            <TableRow key={itemIdx} className="border-none">
                                                <TableCell className="py-2 font-medium text-stone-700">
                                                    {item.nome}
                                                </TableCell>
                                                <TableCell className="py-2 text-right text-stone-600">
                                                    {item.quantidade} {item.unidade}
                                                </TableCell>
                                                <TableCell className="py-2 text-right text-stone-600">
                                                    {formatCurrency(item.custo)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                ))}

                {filtered.length === 0 && (
                    <div className="bg-white rounded-2xl border border-stone-200 p-12 text-center">
                        <FileText className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                        <p className="text-stone-500">Nenhuma nota fiscal encontrada</p>
                        <p className="text-sm text-stone-400 mt-1">
                            Registre entradas por NF para ver o histórico aqui
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
